/**
 * Vuex module for SpreadsheetEditor ephemeral state.
 * NOT persisted — resets on page reload.
 *
 * History entries are either:
 *   { rowId, field, oldVal, newVal }              — single cell edit
 *   { batch: [{ rowId, field, oldVal, newVal }] } — batch edit (one undo step)
 */

const state = () => ({
  // Pending cell changes: { [rowId]: { [field]: newVal } }
  edits: {},
  // Undo/redo history
  history: [],
  // Points to the last applied history entry (-1 = nothing applied)
  historyIndex: -1,
  // Selected row IDs for bulk operations
  selectedRows: [],
  // Last row ID clicked (for shift-click range selection)
  lastClickedRow: null,
  // Clipboard for copy/paste: { value } | null
  clipboard: null,
})

const getters = {
  isDirty: (state) => Object.keys(state.edits).length > 0,

  editCount: (state) =>
    Object.values(state.edits).reduce(
      (sum, fields) => sum + Object.keys(fields).length,
      0,
    ),

  canUndo: (state) => state.historyIndex >= 0,
  canRedo: (state) => state.historyIndex < state.history.length - 1,

  // Return the current value for a cell (edit override or undefined)
  cellEdit: (state) => (rowId, field) =>
    state.edits[rowId]?.[field],
}

const mutations = {
  APPLY_EDIT(state, { rowId, field, oldVal, newVal }) {
    // Write to edits map
    if (!state.edits[rowId]) {
      state.edits = { ...state.edits, [rowId]: {} }
    }
    state.edits = {
      ...state.edits,
      [rowId]: { ...state.edits[rowId], [field]: newVal },
    }

    // Truncate any redo tail, then push
    state.history = state.history.slice(0, state.historyIndex + 1)
    state.history.push({ rowId, field, oldVal, newVal })
    state.historyIndex = state.history.length - 1
  },

  APPLY_BATCH_EDIT(state, { edits }) {
    edits.forEach(({ rowId, field, newVal }) => _writeEdit(state, rowId, field, newVal))
    state.history = state.history.slice(0, state.historyIndex + 1)
    state.history.push({ batch: edits })
    state.historyIndex = state.history.length - 1
  },

  UNDO(state) {
    if (state.historyIndex < 0) return
    const entry = state.history[state.historyIndex]
    state.historyIndex -= 1
    if (entry.batch) {
      entry.batch.forEach(({ rowId, field, oldVal }) => _writeEdit(state, rowId, field, oldVal))
    } else {
      _writeEdit(state, entry.rowId, entry.field, entry.oldVal)
    }
  },

  REDO(state) {
    if (state.historyIndex >= state.history.length - 1) return
    state.historyIndex += 1
    const entry = state.history[state.historyIndex]
    if (entry.batch) {
      entry.batch.forEach(({ rowId, field, newVal }) => _writeEdit(state, rowId, field, newVal))
    } else {
      _writeEdit(state, entry.rowId, entry.field, entry.newVal)
    }
  },

  CLEAR_EDITS(state) {
    state.edits = {}
    state.history = []
    state.historyIndex = -1
  },

  // Row selection
  TOGGLE_ROW(state, rowId) {
    const idx = state.selectedRows.indexOf(rowId)
    if (idx === -1) {
      state.selectedRows = [...state.selectedRows, rowId]
    } else {
      state.selectedRows = state.selectedRows.filter((id) => id !== rowId)
    }
    state.lastClickedRow = rowId
  },

  RANGE_SELECT_ROWS(state, ids) {
    // Add all ids to selection without duplicates
    const set = new Set([...state.selectedRows, ...ids])
    state.selectedRows = Array.from(set)
    state.lastClickedRow = ids[ids.length - 1] ?? state.lastClickedRow
  },

  SET_SELECTED_ROWS(state, ids) {
    state.selectedRows = ids
  },

  CLEAR_SELECTION(state) {
    state.selectedRows = []
    state.lastClickedRow = null
  },

  SET_LAST_CLICKED_ROW(state, rowId) {
    state.lastClickedRow = rowId
  },

  // Clipboard
  SET_CLIPBOARD(state, value) {
    state.clipboard = { value }
  },

  CLEAR_CLIPBOARD(state) {
    state.clipboard = null
  },
}

// Helper: write a value into edits without touching history
function _writeEdit(state, rowId, field, value) {
  if (!state.edits[rowId]) {
    state.edits = { ...state.edits, [rowId]: {} }
  }
  state.edits = {
    ...state.edits,
    [rowId]: { ...state.edits[rowId], [field]: value },
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
}
